import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PROPERTIES } from '../data/properties';
import { ArrowLeft, Bed, Bath, Square, Calendar, Maximize, Check } from 'lucide-react';
export function PropertyDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const property = PROPERTIES.find(p => p.id === id);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  if (!property) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6 text-center" data-id="element-111">
        <h1 className="text-4xl font-light text-charcoal-900 mb-4" data-id="element-112">
          Property Not Found
        </h1>
        <p className="text-charcoal-500 mb-8 max-w-md" data-id="element-113">
          The listing you are looking for may have been removed or is no longer
          available.
        </p>
        <Link to="/" className="px-8 py-4 bg-charcoal-900 text-white rounded-full hover:bg-charcoal-800 transition-colors font-medium" data-id="element-114">
          Return to Collection
        </Link>
      </div>;
  }
  return <main className="bg-white pb-24" data-id="element-115">
      {/* Hero Image */}
      <div className="relative h-[60vh] md:h-[80vh] w-full bg-stone-100" data-id="element-116">
        <img src={property.image} alt={property.address} className="w-full h-full object-cover" data-id="element-117" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal-900/40 via-transparent to-charcoal-900/60" data-id="element-118" />

        {/* Back Button */}
        <div className="absolute top-24 left-6 md:left-12 z-10" data-id="element-119">
          <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white transition-colors font-medium tracking-wide bg-charcoal-900/20 backdrop-blur-md px-4 py-2 rounded-full" data-id="element-120">
            <ArrowLeft size={18} data-id="element-121" />
            <span data-id="element-122">Back to Collection</span>
          </Link>
        </div>

        {/* Header Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-7xl mx-auto w-full flex flex-col md:flex-row md:items-end justify-between gap-6" data-id="element-123">
          <div className="text-white" data-id="element-124">
            <h1 className="text-4xl md:text-6xl font-light tracking-tight mb-2" data-id="element-125">
              {property.address}
            </h1>
            <p className="text-lg md:text-xl text-white/80 font-medium tracking-wide" data-id="element-126">
              {property.city}
            </p>
          </div>
          <div className="text-white md:text-right" data-id="element-127">
            <div className="text-3xl md:text-5xl font-medium tracking-tight" data-id="element-128">
              {property.price}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16 grid grid-cols-1 lg:grid-cols-3 gap-16" data-id="element-129">
        {/* Main Content */}
        <div className="lg:col-span-2 flex flex-col gap-16" data-id="element-130">
          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 py-8 border-y border-stone-200" data-id="element-131">
            <div className="flex flex-col gap-1" data-id="element-132">
              <span className="text-charcoal-400 text-sm font-medium uppercase tracking-widest flex items-center gap-2" data-id="element-133">
                <Bed size={14} data-id="element-134" /> Beds
              </span>
              <span className="text-2xl font-light text-charcoal-900" data-id="element-135">
                {property.beds}
              </span>
            </div>
            <div className="flex flex-col gap-1" data-id="element-136">
              <span className="text-charcoal-400 text-sm font-medium uppercase tracking-widest flex items-center gap-2" data-id="element-137">
                <Bath size={14} data-id="element-138" /> Baths
              </span>
              <span className="text-2xl font-light text-charcoal-900" data-id="element-139">
                {property.baths}
              </span>
            </div>
            <div className="flex flex-col gap-1" data-id="element-140">
              <span className="text-charcoal-400 text-sm font-medium uppercase tracking-widest flex items-center gap-2" data-id="element-141">
                <Square size={14} data-id="element-142" /> Sqft
              </span>
              <span className="text-2xl font-light text-charcoal-900" data-id="element-143">
                {property.sqft}
              </span>
            </div>
            <div className="flex flex-col gap-1" data-id="element-144">
              <span className="text-charcoal-400 text-sm font-medium uppercase tracking-widest flex items-center gap-2" data-id="element-145">
                <Maximize size={14} data-id="element-146" /> Lot
              </span>
              <span className="text-2xl font-light text-charcoal-900" data-id="element-147">
                {property.lotSize}
              </span>
            </div>
            <div className="flex flex-col gap-1" data-id="element-148">
              <span className="text-charcoal-400 text-sm font-medium uppercase tracking-widest flex items-center gap-2" data-id="element-149">
                <Calendar size={14} data-id="element-150" /> Built
              </span>
              <span className="text-2xl font-light text-charcoal-900" data-id="element-151">
                {property.yearBuilt}
              </span>
            </div>
          </div>

          {/* Description */}
          <div data-id="element-152">
            <h2 className="text-2xl font-light text-charcoal-900 mb-6" data-id="element-153">
              About this Property
            </h2>
            <p className="text-lg text-charcoal-600 leading-relaxed font-light" data-id="element-154">
              {property.description}
            </p>
          </div>

          {/* Amenities */}
          <div data-id="element-155">
            <h2 className="text-2xl font-light text-charcoal-900 mb-6" data-id="element-156">
              Amenities & Features
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8" data-id="element-157">
              {property.amenities.map((amenity, idx) => <div key={idx} className="flex items-center gap-3 text-charcoal-600" data-id="element-158">
                  <Check size={18} className="text-charcoal-400" data-id="element-159" />
                  <span className="text-lg font-light" data-id="element-160">{amenity}</span>
                </div>)}
            </div>
          </div>

          {/* Gallery Grid */}
          <div data-id="element-161">
            <h2 className="text-2xl font-light text-charcoal-900 mb-6" data-id="element-162">
              Gallery
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-163">
              {property.gallery.map((img, idx) => <div key={idx} className={`relative bg-stone-100 rounded-xl overflow-hidden ${idx === 0 ? 'md:col-span-2 aspect-[21/9]' : 'aspect-[4/3]'}`} data-id="element-164">
                  <img src={img} alt={`Gallery image ${idx + 1}`} className="w-full h-full object-cover" data-id="element-165" />
                </div>)}
            </div>
          </div>
        </div>

        {/* Sidebar / Agent Panel */}
        <div className="lg:col-span-1" data-id="element-166">
          <div className="sticky top-32 bg-stone-50 rounded-2xl p-8 border border-stone-100" data-id="element-167">
            <h3 className="text-xl font-light text-charcoal-900 mb-6" data-id="element-168">
              Interested in this property?
            </h3>

            <div className="flex items-center gap-4 mb-8" data-id="element-169">
              <img src={property.agent.image} alt={property.agent.name} className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm" data-id="element-170" />
              <div data-id="element-171">
                <div className="font-medium text-charcoal-900 text-lg" data-id="element-172">
                  {property.agent.name}
                </div>
                <div className="text-charcoal-500 text-sm" data-id="element-173">
                  {property.agent.title}
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-8" data-id="element-174">
              <button className="w-full py-4 bg-charcoal-900 text-white rounded-xl hover:bg-charcoal-800 transition-colors font-medium text-lg" data-id="element-175">
                Request a Private Viewing
              </button>
              <button className="w-full py-4 bg-white text-charcoal-900 border border-stone-200 rounded-xl hover:bg-stone-50 transition-colors font-medium text-lg" data-id="element-176">
                Contact Advisor
              </button>
            </div>

            <div className="text-center text-sm text-charcoal-400 font-light" data-id="element-177">
              Or call directly at <br data-id="element-178" />
              <a href={`tel:${property.agent.phone}`} className="text-charcoal-900 font-medium hover:underline mt-1 inline-block" data-id="element-179">
                {property.agent.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>;
}